// @ts-strict-ignore
import useLocalStorage from '@dashboard/hooks/useLocalStorage';
import { createContext, type ReactNode, useEffect, useState } from 'react';
import { IntlProvider, ReactIntlErrorCode } from 'react-intl';

export enum Locale {
  EN = 'en',
  DE = 'de',
  ES_419 = 'es-419',
  ES_ES = 'es-ES',
  FR = 'fr',
  IT = 'it',
  JA = 'ja',
  KO = 'ko',
  PL = 'pl',
  PT_BR = 'pt-BR',
  RU = 'ru',
  ZH_HANT = 'zh-Hant',
  ZH_CN = 'zh-CN',
}

interface StructuredMessage {
  context?: string;
  string: string;
}
type LocaleMessages = Record<string, StructuredMessage>;

export const localeNames: Record<Locale, string> = {
  [Locale.EN]: 'English',
  [Locale.DE]: 'Deutsch',
  [Locale.ES_419]: 'Español (Latinoamérica)',
  [Locale.ES_ES]: 'Español (España)',
  [Locale.FR]: 'Français',
  [Locale.IT]: 'Italiano',
  [Locale.JA]: '日本語',
  [Locale.KO]: '한국어',
  [Locale.PL]: 'Polski',
  [Locale.PT_BR]: 'Português (Brasil)',
  [Locale.RU]: 'Русский',
  [Locale.ZH_HANT]: '繁體中文',
  [Locale.ZH_CN]: '简体中文',
};

const dotSeparator = '_dot_';
const sepRegExp = new RegExp(dotSeparator, 'g');

function getKeyValueJson(messages: LocaleMessages): Record<string, string> {
  if (messages) {
    const keyValueMessages: Record<string, string> = {};

    return Object.entries(messages).reduce((acc, [id, msg]) => {
      acc[id.replace(sepRegExp, '.')] = msg.string;

      return acc;
    }, keyValueMessages);
  }
}

const localeCode = process.env.LOCALE_CODE || 'EN';
const defaultLocale = Locale[localeCode];

interface LocaleContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}
export const LocaleContext = createContext<LocaleContextType>({
  locale: defaultLocale,
  setLocale: () => undefined,
});

const { Consumer: LocaleConsumer, Provider: RawLocaleProvider } = LocaleContext;
const LocaleProvider = ({ children }: { children: ReactNode }) => {
  const [locale, setLocale] = useLocalStorage('locale', defaultLocale);
  const [messages, setMessages] = useState(undefined);

  useEffect(() => {
    async function changeLocale() {
      if (locale !== Locale.EN) {
        const mod = await import(`../../../locale/${locale}.json`);

        setMessages(mod.default);
      } else {
        setMessages(undefined);
      }
    }

    changeLocale();
  }, [locale]);

  return (
    <IntlProvider
      defaultLocale={defaultLocale}
      locale={locale}
      messages={getKeyValueJson(messages)}
      onError={(err) => {
        if (!(err.code === ReactIntlErrorCode.MISSING_TRANSLATION)) {
          console.error(err);
        }
      }}
      key={locale}
    >
      <RawLocaleProvider
        value={{
          locale,
          setLocale,
        }}
      >
        {children}
      </RawLocaleProvider>
    </IntlProvider>
  );
};

export { LocaleConsumer, LocaleProvider, RawLocaleProvider };
